/**
 * 大厅（HomePage）— Optical Tactical 重构版。
 *
 * 信息结构保持不变（身份 / 创建 / 加入 / 演示 / 能力说明），仅替换视觉语言：
 * - 黑曜石画布 + 全局体积光束（AmbientBackdrop，由 App 挂载）；
 * - 品牌区用冷白金属大字 + 真实 canvas Matrix Orb，不再用霓虹胶囊；
 * - 表单与能力说明采用 75% 光学透镜 + 发丝分割，不画嵌套实线框。
 */
import { useState } from 'react';
import {
  isValidRoomCode,
  normalizeRoomCode,
  sideLabel,
  SEATS,
  type Seat,
  type Side,
} from '@zongheng/core-schema';
import { TopBar } from '../app/TopBar.tsx';
import { useArena } from '../state/ArenaContext.tsx';
import type { IdentityInput } from '../state/types.ts';
import {
  loadIdentity,
  normalizeIdentity,
  parseJoinCode,
  readRoute,
  saveIdentity,
} from '../app/identity.ts';
import { makeRoomCode } from '../components/common/demoBootstrap.ts';
import { Badge, Button, cx, KeyHint, Panel } from '../components/ui/primitives.tsx';
import { MatrixOrb } from '../components/system/MatrixOrb.tsx';
import { CornerReticles } from '../components/system/Reticles.tsx';
import { updateSettings, useSettings, type VideoFps } from '../state/settings.ts';

type Role = IdentityInput['role'];

const SEAT_LABEL: Record<Seat, string> = {
  1: '一辩',
  2: '二辩',
  3: '三辩',
  4: '四辩',
};

const CAPABILITIES: { title: string; detail: string }[] = [
  {
    title: '零云端 · P2P 星型',
    detail: '教师端持有唯一权威 XState 状态机与主时钟；辩手之间 WebRTC 直连，公共状态经 Yjs CRDT 同步，刷新可恢复现场。',
  },
  {
    title: '端侧确定性引擎',
    detail: 'Aho-Corasick + 数值正则断言毫秒级抓「数据张冠李戴」；MiniSearch 中文 BM25 支持 ⌘K 秒查原始论据。',
  },
  {
    title: '双盲 Commit-Reveal',
    detail: '底牌 SHA-256 承诺广播、私有 DataChannel 单播明文，配对结束才 Reveal；F12 也偷不到对手卡片。',
  },
  {
    title: 'Phase 2 可插拔插槽',
    detail: 'ASR / LLM / TTS 以 Adapter 抽象预留；当前发言由真人手动录入模拟，二期直接挂载流式语音与 CAG。',
  },
];

const INPUT_CLASS =
  'w-full rounded-lg bg-white/[0.03] px-3 py-2.5 text-[13px] text-slate-100 outline-none shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),inset_0_0_0_1px_rgba(255,255,255,0.05)] transition placeholder:text-slate-600 focus:bg-white/[0.05] focus:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.16),inset_0_0_0_1px_rgba(125,211,252,0.3)]';

export default function HomePage() {
  const { actions } = useArena();
  const settings = useSettings();
  const [createOpen, setCreateOpen] = useState(false);

  const [name, setName] = useState(() => loadIdentity()?.name ?? '');
  const [role, setRole] = useState<Role>(() => loadIdentity()?.role ?? 'DEBATER');
  const [side, setSide] = useState<Side>(() => loadIdentity()?.side ?? 'AFF');
  const [seat, setSeat] = useState<Seat>(() => loadIdentity()?.seat ?? 1);
  const [joinCode, setJoinCode] = useState(() => parseJoinCode(readRoute()) ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isDebater = role === 'DEBATER';

  function buildIdentity(): IdentityInput | null {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('请先填写姓名（用于房间内显示与战报署名）');
      return null;
    }
    if (trimmedName.length > 16) {
      setError('姓名请控制在 16 个字符以内');
      return null;
    }
    return {
      name: trimmedName,
      role,
      side: isDebater ? side : undefined,
      seat: isDebater ? seat : undefined,
    };
  }

  function persist(identity: IdentityInput): void {
    saveIdentity(normalizeIdentity(identity));
  }

  async function handleCreateRoom(): Promise<void> {
    const identity = buildIdentity();
    if (!identity) return;
    setError(null);
    setBusy(true);
    try {
      await actions.createRoom(identity, makeRoomCode());
      persist(identity);
    } catch (err) {
      setError(`创建房间失败：${err instanceof Error ? err.message : String(err)}（可尝试 ?demo=1 离线演示）`);
    } finally {
      setBusy(false);
    }
  }

  async function handleJoinRoom(): Promise<void> {
    const identity = buildIdentity();
    if (!identity) return;
    const code = normalizeRoomCode(joinCode);
    if (!isValidRoomCode(code)) {
      setError('房间码应为 6 位字母或数字，例如 ZXCV12');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await actions.joinRoom(code, identity);
      persist(identity);
    } catch (err) {
      setError(`加入房间失败：${err instanceof Error ? err.message : String(err)}（可尝试 ?demo=1 离线演示）`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDemoStart(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const identity: IdentityInput = {
        name: name.trim() || '演示主持人',
        role: 'TEACHER',
      };
      await actions.createRoom(identity, makeRoomCode());
      actions.setRoute('arena');
      actions.startMatch();
    } catch (err) {
      setError(`演示模式启动失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="home-root" className="min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-[1480px] px-5 pb-16 pt-8 sm:pt-12">
        {/* ---------------------------- 品牌 Hero ---------------------------- */}
        <section className="relative grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="relative">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/[0.035] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18),inset_0_0_0_1px_rgba(255,255,255,0.05)]">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="8.4" stroke="white" strokeOpacity="0.6" strokeWidth="1.1" strokeDasharray="4 2.6" />
                  <circle cx="12" cy="12" r="2.4" fill="white" />
                  <path d="M12 1.6v3.8M12 18.6v3.8M1.6 12h3.8M18.6 12h3.8" stroke="white" strokeWidth="1.1" />
                </svg>
              </span>
              <span className="micro-label">OPTICAL TACTICAL SYSTEM · PHASE 1</span>
            </div>

            <h1 className="mt-6 text-[44px] font-semibold leading-[1.04] tracking-[-0.02em] text-metal sm:text-[64px]">
              纵横演武场
            </h1>
            <p className="mt-4 max-w-xl text-[13px] leading-relaxed text-slate-400">
              双盲抗压训练 · 纵横杯改良奥瑞冈制 16 环节 · 纯端侧 P2P。
              教师中心星型仲裁，辩手间媒体网格直连；底牌承诺-揭示，确定性口径核验。
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5">
                <KeyHint>Space</KeyHint> 切换发言权
              </span>
              <span className="flex items-center gap-1.5">
                <KeyHint>Tab</KeyHint> 隐藏锦囊
              </span>
              <span className="flex items-center gap-1.5">
                <KeyHint>1/2/3</KeyHint> 必打论据
              </span>
              <span className="flex items-center gap-1.5">
                <KeyHint>⌘K</KeyHint> 论据检索
              </span>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-2">
              <Badge tone="pass">端侧可用</Badge>
              <Badge tone="blind">Commit-Reveal</Badge>
              <Badge tone="aff">16 环节状态机</Badge>
              <span className="mono text-[10px] tracking-[0.18em] text-slate-600">V3.0 OPTICAL</span>
            </div>
          </div>

          <div className="relative hidden h-[240px] items-center justify-center lg:flex">
            <div className="absolute inset-0 rounded-[22px] bg-white/[0.012] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07)]" />
            <CornerReticles inset={14} />
            <MatrixOrb active particles={780} accent="neutral" width={300} height={230} opacity={0.92} />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
              <div className="micro-label">MATRIX CORE · STANDBY</div>
              <div className="mt-1 text-[10px] text-slate-600">真实 canvas 3D 点阵 · 非纯 CSS</div>
            </div>
          </div>
        </section>

        <div className="hairline mt-10" />

        {/* ------------------------- 身份 / 房间 / 能力 ------------------------- */}
        <div className="mt-8 grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
          <Panel
            title="入场身份"
            subtitle="身份保存在本机，离线可用；房间内显示姓名与阵营"
            accent="NEUTRAL"
            testId="home-identity"
          >
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_240px]">
                <div>
                  <label htmlFor="name-input" className="micro-label mb-2 block">
                    姓名 / CALLSIGN <span className="text-[#FB7185]">*</span>
                  </label>
                  <input
                    id="name-input"
                    data-testid="name-input"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="例如：林小满"
                    maxLength={16}
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <span className="micro-label mb-2 block">角色 / ROLE</span>
                  <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-white/[0.025] p-1 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                    <RoleButton
                      active={role === 'DEBATER'}
                      testId="role-debater"
                      title="辩手"
                      onClick={() => setRole('DEBATER')}
                    />
                    <RoleButton
                      active={role === 'TEACHER'}
                      testId="role-teacher"
                      title="教师"
                      onClick={() => setRole('TEACHER')}
                    />
                  </div>
                </div>
              </div>

              {isDebater && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <span className="micro-label mb-2 block">阵营 / SIDE</span>
                    <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-white/[0.025] p-1 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]">
                      <ToggleButton active={side === 'AFF'} testId="side-aff" tone="aff" onClick={() => setSide('AFF')}>
                        {sideLabel('AFF')}
                      </ToggleButton>
                      <ToggleButton active={side === 'NEG'} testId="side-neg" tone="neg" onClick={() => setSide('NEG')}>
                        {sideLabel('NEG')}
                      </ToggleButton>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="seat-select" className="micro-label mb-2 block">
                      辩位 / SEAT
                    </label>
                    <select id="seat-select" data-testid="seat-select" value={seat} onChange={(event) => setSeat(Number(event.target.value) as Seat)} className={INPUT_CLASS}>
                      {SEATS.map((value) => (
                        <option key={value} value={value} className="bg-[#0c0d12]">
                          {value} 辩 · {SEAT_LABEL[value]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {!isDebater && (
                <p className="text-[11px] leading-relaxed text-slate-500">
                  教师身份创建房间后进入教练台：负责开赛、推进 16 环节、暂停 / 继续与强制揭示。
                </p>
              )}

              {error && (
                <p data-testid="home-error" role="alert" className="flex items-start gap-2 text-[11px] leading-relaxed text-[#FECDD3]">
                  <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-[#FB7185] shadow-[0_0_8px_rgba(251,113,133,0.8)]" />
                  {error}
                </p>
              )}

              <div className="hairline" />

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-slate-100">
                      {isDebater ? '创建练习房' : '创建房间'}
                    </span>
                    <span className="micro-label">HOST</span>
                  </div>
                  <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                    {isDebater
                      ? '生成 6 位房间码，先进入作战台等教师；教师加入后接管权威计时与发牌。'
                      : '生成 6 位房间码并作为房间创建者进入；辩手凭房间码或链接加入。'}
                  </p>
                  <Button variant="primary" size="lg" onClick={() => setCreateOpen(true)} disabled={busy} testId="create-room" className="mt-4 w-full">
                    {isDebater ? '创建练习房（等待教师）' : '创建房间'}
                  </Button>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-medium text-slate-100">加入房间</span>
                    <span className="micro-label">#/JOIN/CODE</span>
                  </div>
                  <p className="mt-2 text-[11px] leading-relaxed text-slate-500">输入教师端展示的 6 位房间码，入座对应阵营与辩位。</p>
                  <div className="mt-4 flex gap-2">
                    <input
                      data-testid="join-code"
                      value={joinCode}
                      onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
                      placeholder="6 位房间码"
                      maxLength={8}
                      aria-label="房间码"
                      className={cx(INPUT_CLASS, 'mono flex-1 tracking-[0.22em]')}
                    />
                    <Button variant="secondary" size="lg" onClick={() => void handleJoinRoom()} disabled={busy} testId="join-room" className="shrink-0">
                      加入
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-[12px] text-slate-200">
                    <span className="status-dot" />
                    离线演示开赛
                  </div>
                  <p className="mt-1 text-[10px] text-slate-500">
                    本地 Mock 后端一键发牌开赛，不联网；也可直接访问{' '}
                    <a href="?demo=1" className="text-[#C7D2FE] underline decoration-white/20">
                      ?demo=1
                    </a>
                    。
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => void handleDemoStart()} disabled={busy} testId="start-hud" title="创建本地演示房间并立即进入作战台">
                  进入作战台 →
                </Button>
              </div>
            </div>
          </Panel>

          <div className="space-y-5">
            <Panel title="Phase 1 能力地图" subtitle="端侧确定性 · 零云端依赖" accent="BLIND" testId="home-capabilities">
              <ul>
                {CAPABILITIES.map((capability, index) => (
                  <li key={capability.title} className={cx('py-3', index > 0 && 'border-t border-white/[0.045]')}>
                    <div className="flex items-center gap-2">
                      <span className="status-dot" />
                      <span className="text-[12px] font-medium text-slate-200">{capability.title}</span>
                    </div>
                    <p className="clamp-3 mt-1.5 pl-3.5 text-[11px] leading-relaxed text-slate-500">{capability.detail}</p>
                  </li>
                ))}
              </ul>
            </Panel>

            <Panel title="双盲训练流程" subtitle="6 组申论质询 + 2 轮 1v1 + 双四辩结辩">
              <ol className="space-y-3">
                {[
                  '创建 / 加入房间，辩手按阵营与辩位入座；教师端为唯一权威节点。',
                  '开赛即承诺发牌：申论方得《申论策略卡》，质询方得《质询任务卡》，双方互不可见。',
                  '双表 Chess Clock 交替计时；Space 切发言权，Tab 展开反杀锦囊，1/2/3 勾选必打论据。',
                  '配对结束自动 Reveal：双方底牌摘要与哈希校验同时公开，进入复盘并导出战报。',
                ].map((text, index) => (
                  <li key={text} className="flex gap-3 text-[11px] leading-relaxed text-slate-400">
                    <span className="mono shrink-0 text-[10px] text-slate-600">{String(index + 1).padStart(2, '0')}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
            </Panel>
          </div>
        </div>
      </main>

      {createOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm" data-testid="create-room-dialog">
          <section className="lens w-full max-w-[560px] p-5">
            <header className="flex items-start justify-between gap-3">
              <div>
                <div className="micro-label">ROOM SETUP</div>
                <h2 className="mt-1 text-[18px] font-semibold text-slate-100">
                  {isDebater ? '创建练习房' : '创建房间'}
                </h2>
                <p className="mt-1 text-[11px] text-slate-500">
                  先确认本场设置；创建后仍可在大厅/教练台调整。
                </p>
              </div>
              <span className="pill">{name.trim() || '未填写姓名'}</span>
            </header>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="rounded-md bg-white/[0.025] px-3 py-2">
                <div className="text-[11px] text-slate-300">房间容量</div>
                <select
                  data-testid="create-capacity"
                  value={settings.roomCapacity}
                  onChange={(event) => updateSettings({ roomCapacity: Number(event.target.value) as 4 | 8 | 12 })}
                  className="mt-2 w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[12px] text-slate-200"
                >
                  {[4, 8, 12].map((capacity) => (
                    <option key={capacity} value={capacity}>{capacity} 人</option>
                  ))}
                </select>
              </label>
              <label className="rounded-md bg-white/[0.025] px-3 py-2">
                <div className="text-[11px] text-slate-300">媒体策略</div>
                <select
                  data-testid="create-media-policy"
                  value={settings.mediaPolicy}
                  onChange={(event) => updateSettings({ mediaPolicy: event.target.value as 'audio-only' | 'audio-video' })}
                  className="mt-2 w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[12px] text-slate-200"
                >
                  <option value="audio-video">音视频</option>
                  <option value="audio-only">仅音频</option>
                </select>
              </label>
              <label className="rounded-md bg-white/[0.025] px-3 py-2">
                <div className="text-[11px] text-slate-300">视频帧率</div>
                <select
                  data-testid="create-video-fps"
                  value={settings.videoFps}
                  onChange={(event) => updateSettings({ videoFps: Number(event.target.value) as VideoFps })}
                  className="mt-2 w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[12px] text-slate-200"
                >
                  {[15, 24, 30].map((fps) => (
                    <option key={fps} value={fps}>{fps} fps</option>
                  ))}
                </select>
              </label>
              <div className="rounded-md bg-white/[0.025] px-3 py-2 text-[10px] leading-relaxed text-slate-500">
                容量最终受信令 Worker 上限约束；媒体策略只影响本端默认采集。
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCreateOpen(false)} testId="cancel-create-room">
                取消
              </Button>
              <Button
                variant="primary"
                size="sm"
                testId="confirm-create-room"
                disabled={busy}
                onClick={() => {
                  setCreateOpen(false);
                  void handleCreateRoom();
                }}
              >
                确认创建
              </Button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function RoleButton({
  active,
  testId,
  title,
  onClick,
}: {
  active: boolean;
  testId: string;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'rounded-md px-3 py-1.5 text-[12px] font-medium transition-all',
        active
          ? 'bg-white/[0.1] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18),0_8px_20px_-12px_rgba(255,255,255,0.4)]'
          : 'text-slate-500 hover:text-slate-300',
      )}
    >
      {title}
    </button>
  );
}

function ToggleButton({
  active,
  testId,
  tone,
  children,
  onClick,
}: {
  active: boolean;
  testId: string;
  tone: 'aff' | 'neg';
  children: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-all',
        active
          ? tone === 'aff'
            ? 'bg-[#7DD3FC]/10 text-[#BAE6FD] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14),inset_0_0_0_1px_rgba(125,211,252,0.18)]'
            : 'bg-[#FDA4AF]/10 text-[#FECDD3] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14),inset_0_0_0_1px_rgba(253,164,175,0.18)]'
          : 'text-slate-500 hover:text-slate-300',
      )}
    >
      {active && <span className={cx('h-1 w-1 rounded-full', tone === 'aff' ? 'bg-[#BAE6FD] shadow-[0_0_8px_rgba(186,230,253,0.8)]' : 'bg-[#FDA4AF] shadow-[0_0_8px_rgba(253,164,175,0.8)]')} />}
      {children}
    </button>
  );
}
